import AcquisitionChannel from "../../../model/acquisitionChannel/AcquisitionChannel";
import {
	ACQUISITION_CHANNEL_CREATED,
	ACQUISITION_CHANNEL_FETCHED,
	ACQUISITION_CHANNEL_SORT_ORDER_UPDATED,
	ACQUISITION_CHANNEL_UPDATED
} from "./actionType";
import {Action} from "redux-actions";
import {OnFailure, OnSuccess, OnSuccessData} from "../actionHelpers";
import {api} from "../../../api/api";
import {Dispatch} from "react-redux";

/**
 * Acquisition channel fetched successful action.
 * @param {Array<AcquisitionChannel>} acquisitionChannels all acquisition channels from server.
 * @return {Action<ACQUISITION_CHANNEL_FETCHED>}
 */
function acquisitionChannelFetched(acquisitionChannels: Array<AcquisitionChannel>): Action<ACQUISITION_CHANNEL_FETCHED> {
	return {type: ACQUISITION_CHANNEL_FETCHED, payload: {acquisitionChannels}};
}

/**
 * Acquisition channel created successful action.
 * @return {Action<ACQUISITION_CHANNEL_CREATED>}
 * @param acquisitionChannel
 */
function acquisitionChannelCreated(acquisitionChannel: AcquisitionChannel): Action<ACQUISITION_CHANNEL_CREATED> {
	return {type: ACQUISITION_CHANNEL_CREATED, payload: {acquisitionChannel}};
}

/**
 * Acquisition channel updated successful action.
 * @return {Action<ACQUISITION_CHANNEL_UPDATED>}
 * @param acquisitionChannel
 */
function acquisitionChannelUpdated(acquisitionChannel: AcquisitionChannel): Action<ACQUISITION_CHANNEL_UPDATED> {
	return {type: ACQUISITION_CHANNEL_UPDATED, payload: {acquisitionChannel}};
}

/**
 * Update acquisition channels sort order successful action.
 * @param {Array<AcquisitionChannel>} acquisitionChannels all acquisition channels from server.
 * @return {Action<ACQUISITION_CHANNEL_SORT_ORDER_UPDATED>}
 */
function acquisitionSortOrderUpdated(acquisitionChannels: Array<AcquisitionChannel>): Action<ACQUISITION_CHANNEL_SORT_ORDER_UPDATED> {
	return {type: ACQUISITION_CHANNEL_SORT_ORDER_UPDATED, payload: {acquisitionChannels}};
}

/**
 * Fetch acquisition channels from server.
 * @param {OnSuccessData} onSuccess
 * @param {OnFailure} onFailure
 * @return {(dispatch: Dispatch<any>) => void}
 */
export function fetchAcquisitionChannels(onSuccess: OnSuccessData<AcquisitionChannel[]>, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.acquisitionChannelApi.fetchAcquisitionChannels().then(acquisitionChannels => {
			dispatch(acquisitionChannelFetched(acquisitionChannels));
			onSuccess(acquisitionChannels);
		}).catch(onFailure);
	};
}

/**
 * Create acquisition channel.
 * @param acquisitionChannel
 * @param {OnSuccessData} onSuccess
 * @param {OnFailure} onFailure
 * @return {(dispatch: Dispatch<any>) => void}
 */
export function createAcquisitionChannel(acquisitionChannel: AcquisitionChannel, onSuccess: OnSuccessData<AcquisitionChannel>, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.acquisitionChannelApi.createAcquisitionChannel(acquisitionChannel).then(acquisitionChannel => {
			dispatch(acquisitionChannelCreated(acquisitionChannel));
			onSuccess(acquisitionChannel);
		}).catch(onFailure);
	};
}

/**
 * Update acquisition channel.
 * @param acquisitionChannelId
 * @param acquisitionChannel
 * @param {OnSuccessData} onSuccess
 * @param {OnFailure} onFailure
 * @return {(dispatch: Dispatch<any>) => void}
 */
export function updateAcquisitionChannel(
	acquisitionChannelId: string,
	acquisitionChannel: AcquisitionChannel,
	onSuccess: OnSuccessData<AcquisitionChannel>,
	onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.acquisitionChannelApi.updateAcquisitionChannel(acquisitionChannelId, acquisitionChannel).then(acquisitionChannel => {
			dispatch(acquisitionChannelUpdated(acquisitionChannel));
			onSuccess(acquisitionChannel);
		}).catch(onFailure);
	};
}

/**
 * Update acquisition channel sort order.
 * @param acquisitionChannels
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @return {(dispatch: Dispatch<any>) => void}
 */
export function updateAcquisitionChannelSortOrder(
	acquisitionChannels: AcquisitionChannel[],
	onSuccess: OnSuccess,
	onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.acquisitionChannelApi.updateSortOrder(acquisitionChannels).then(() => {
			dispatch(acquisitionSortOrderUpdated(acquisitionChannels));
			dispatch(onSuccess);
		}).catch(onFailure);
	};
}
